import React, { useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { QUESTION_KIND, resolveQuestionKind } from "../runtime/questionKinds";
import { useModuleRuntime } from "../runtime/useModuleRuntime";
import OnlyTextQuestion from "../runtime/questions/OnlyTextQuestion";
import TestQuestion from "../runtime/questions/TestQuestion";
import DropdownQuestion from "../runtime/questions/DropdownQuestion";
import Dropdown2Question from "../runtime/questions/Dropdown2Question";
import GameQuestion from "../runtime/questions/GameQuestion";
import GameQuestionDnd2 from "../runtime/questions/GameQuestionDnd2";
import ImageQuizQuestion from "../runtime/questions/ImageQuizQuestion";
import InputQuestion from "../runtime/questions/InputQuestion";
import MatchingPairsQuestion from "../runtime/questions/MatchingPairsQuestion";
import FillwordQuestion from "../runtime/questions/FillwordQuestion";
import PuzzleQuestion from "../runtime/questions/PuzzleQuestion";
import JigsawQuestion from "../runtime/questions/JigsawQuestion";
import WordGridQuestion from "../runtime/questions/WordGridQuestion";
import LogicalSequenceQuestion from "../runtime/questions/LogicalSequenceQuestion";
import PdfQuestion from "../runtime/questions/PdfQuestion";

import "./Module.css";
import "./responsive.css";

const QUESTION_KINDS_WITH_OWN_IMAGE = new Set([
  QUESTION_KIND.TEST,
  QUESTION_KIND.VIDEO,
  QUESTION_KIND.FILLWORD,
  QUESTION_KIND.PUZZLE,
  QUESTION_KIND.JIGSAW,
  QUESTION_KIND.DND,
  QUESTION_KIND.DND2,
]);

const QUESTION_KINDS_WITH_OWN_AUDIO = new Set([
  QUESTION_KIND.IMAGE_QUIZ,
]);

const QuestionMedia = ({ question, kind }) => {
  const showImage = Boolean(question?.imgPath) && !QUESTION_KINDS_WITH_OWN_IMAGE.has(kind);
  const showAudio = Boolean(question?.audioPath) && !QUESTION_KINDS_WITH_OWN_AUDIO.has(kind);

  if (!showImage && !showAudio) return null;

  return (
    <div className="question-media">
      {showImage && (
        <img
          className="question-media-image"
          src={question.imgPath}
          alt=""
        />
      )}
      {showAudio && (
        <audio className="question-media-audio" controls src={question.audioPath}>
          Your browser does not support the audio element.
        </audio>
      )}
    </div>
  );
};

function Module({ module, onBackToTaskTypes }) {
  const title = module?.title || "";
  const questions = useMemo(
    () => (Array.isArray(module?.questions) ? module.questions : []),
    [module]
  );

  const {
    currentQuestion,
    selectedQuestionIndex,
    isLastQuestion,
    outOfAttempts,
    score,
    showModal,
    state,
    selectOption,
    toggleOption,
    closeFeedback,
    setDropdownSelection,
    setInputSelection,
    setGameOver,
    handleScoreUpdate,
    checkAnswer,
    nextQuestion,
    finishQuiz,
    closeModal,
  } = useModuleRuntime(questions);

  if (!currentQuestion) {
    return (
      <div className="test-container">
        {onBackToTaskTypes && (
          <button type="button" className="module-back-link" onClick={onBackToTaskTypes}>
            <span aria-hidden="true">←</span>
            До типів завдань
          </button>
        )}
        <div className="test-title" dangerouslySetInnerHTML={{ __html: title }} />
        <div className="placeholder-content">
          <p>Для даного модуля завдання не призначено</p>
        </div>
      </div>
    );
  }

  const kind = resolveQuestionKind(currentQuestion);
  const containerClassName = [
    "test-container",
    kind === QUESTION_KIND.DND || kind === QUESTION_KIND.DND2 || kind === QUESTION_KIND.MATCHING_PAIRS || kind === QUESTION_KIND.FILLWORD || kind === QUESTION_KIND.PUZZLE || kind === QUESTION_KIND.JIGSAW || kind === QUESTION_KIND.WORD_GRID || kind === QUESTION_KIND.LOGICAL_SEQUENCE || kind === QUESTION_KIND.PDF ? "game" : "",
    kind === QUESTION_KIND.MATCHING_PAIRS ? "matching-pairs-container" : "",
  ].filter(Boolean).join(" ");

  const showNextForTest = (outOfAttempts || state.showNextButton) && !isLastQuestion;
  const showFinishForTest =
    isLastQuestion && (state.showNextButton || Boolean(currentQuestion.isCreativeTask));

  const showNextForDropdown = (outOfAttempts || state.showNextButton) && !isLastQuestion;
  const showFinishForDropdown = isLastQuestion;
  const showNextForDropdown2 = (outOfAttempts || state.showNextButton) && !isLastQuestion;
  const showFinishForDropdown2 = isLastQuestion && state.showNextButton;
  const showNextForInput = (outOfAttempts || state.showNextButton) && !isLastQuestion;
  const showFinishForInput = isLastQuestion && state.showNextButton;
  const showNextForLogicalSequence = (outOfAttempts || state.showNextButton) && !isLastQuestion;
  const showFinishForLogicalSequence = isLastQuestion && state.showNextButton;
  const showNextForPdf = !isLastQuestion;
  const showFinishForPdf = isLastQuestion;

  const showNextForGame = !isLastQuestion;
  const showFinishForGame = isLastQuestion;
  const imageQuizCorrectAnswers = Array.isArray(currentQuestion.correctAnswer)
    ? currentQuestion.correctAnswer.filter(Boolean)
    : currentQuestion.correctAnswer
      ? [currentQuestion.correctAnswer]
      : [];
  const isImageQuizMultipleChoice = imageQuizCorrectAnswers.length > 1;
  const selectedImageQuizOptions = Array.isArray(state.selectedOption)
    ? state.selectedOption
    : state.selectedOption
      ? [state.selectedOption]
      : [];

  return (
    <div className={containerClassName}>
      {showModal && (
        <Modal show={showModal} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Завершення тесту</Modal.Title>
          </Modal.Header>
          <Modal.Body>Ви закінчили модуль. Ваш рахунок: {score} ⭐</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              Закрити
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {onBackToTaskTypes && (
        <button type="button" className="module-back-link" onClick={onBackToTaskTypes}>
          <span aria-hidden="true">←</span>
          До типів завдань
        </button>
      )}
      <div className="test-title" dangerouslySetInnerHTML={{ __html: title }} />
      {currentQuestion.subtitle && <div className="testsubtitle">{currentQuestion.subtitle}</div>}
      <QuestionMedia question={currentQuestion} kind={kind} />

      {kind === QUESTION_KIND.ONLY_TEXT && (
        <OnlyTextQuestion
          text={currentQuestion.text}
          onNext={nextQuestion}
          canGoNext={!isLastQuestion}
        />
      )}

      {(kind === QUESTION_KIND.TEST || kind === QUESTION_KIND.VIDEO) && (
        <TestQuestion
          question={currentQuestion.question}
          options={currentQuestion.options}
          selectedOption={state.selectedOption}
          optionStatuses={state.optionStatuses}
          onOptionClick={selectOption}
          showCheckButton={state.showCheckButton}
          canCheck={Boolean(state.selectedOption)}
          onCheck={() => checkAnswer("test")}
          feedbackVisible={state.feedbackVisible}
          feedback={state.feedback}
          onCloseFeedback={closeFeedback}
          showNextButton={
            showNextForTest ||
            Boolean(currentQuestion.videoPath && currentQuestion.shouldGoNext && !isLastQuestion)
          }
          onNext={nextQuestion}
          showFinishButton={showFinishForTest}
          onFinish={finishQuiz}
          imgPath={currentQuestion.imgPath}
          videoPath={currentQuestion.videoPath}
          addClass={currentQuestion.addClass}
        />
      )}

      {kind === QUESTION_KIND.IMAGE_QUIZ && (
        <ImageQuizQuestion
          question={currentQuestion.question}
          audioPath={currentQuestion.audioPath}
          options={currentQuestion.options}
          selectedOption={state.selectedOption}
          optionStatuses={state.optionStatuses}
          onOptionClick={isImageQuizMultipleChoice ? toggleOption : selectOption}
          showCheckButton={state.showCheckButton}
          canCheck={selectedImageQuizOptions.length > 0}
          onCheck={() => checkAnswer("test")}
          feedbackVisible={state.feedbackVisible}
          feedback={state.feedback}
          onCloseFeedback={closeFeedback}
          showNextButton={showNextForTest}
          onNext={nextQuestion}
          showFinishButton={showFinishForTest}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.DROPDOWN && (
        <DropdownQuestion
          question={currentQuestion.question}
          dropdownQuestionParts={currentQuestion.dropdownQuestionParts}
          options={currentQuestion.options}
          showAnswerOptions={currentQuestion.showAnswerOptions}
          dropdownAmount={currentQuestion.dropdownAmount}
          showCheckButton={state.showCheckButton}
          canCheck={state.allDropdownsSelected}
          onCheck={() => checkAnswer("dropdown")}
          feedbackVisible={state.feedbackVisible}
          feedback={state.feedback}
          onCloseFeedback={closeFeedback}
          showNextButton={showNextForDropdown}
          onNext={nextQuestion}
          showFinishButton={showFinishForDropdown}
          onFinish={finishQuiz}
          onAllDropdownsSelected={setDropdownSelection}
          questionKey={currentQuestion.id || selectedQuestionIndex}
        />
      )}

      {kind === QUESTION_KIND.DROPDOWN2 && (
        <Dropdown2Question
          question={currentQuestion.question}
          dropdowns={currentQuestion.dropdowns}
          showCheckButton={state.showCheckButton}
          canCheck={state.allDropdownsSelected}
          onCheck={() => checkAnswer("dropdown2")}
          feedbackVisible={state.feedbackVisible}
          feedback={state.feedback}
          onCloseFeedback={closeFeedback}
          showNextButton={showNextForDropdown2}
          onNext={nextQuestion}
          showFinishButton={showFinishForDropdown2}
          onFinish={finishQuiz}
          onDropdownsChange={setDropdownSelection}
          questionKey={currentQuestion.id || selectedQuestionIndex}
        />
      )}

      {kind === QUESTION_KIND.INPUT && (
        <InputQuestion
          question={currentQuestion.question}
          questionKey={currentQuestion.id || selectedQuestionIndex}
          showCheckButton={state.showCheckButton}
          canCheck={state.allInputsFilled}
          onCheck={() => checkAnswer("input")}
          feedbackVisible={state.feedbackVisible}
          feedback={state.feedback}
          onCloseFeedback={closeFeedback}
          showNextButton={showNextForInput}
          onNext={nextQuestion}
          showFinishButton={showFinishForInput}
          onFinish={finishQuiz}
          onInputsChange={setInputSelection}
        />
      )}

      {kind === QUESTION_KIND.MATCHING_PAIRS && (
        <MatchingPairsQuestion
          key={selectedQuestionIndex}
          question={currentQuestion.question}
          instruction={currentQuestion.instruction}
          tip={currentQuestion.tip}
          leftTitle={currentQuestion.leftTitle}
          rightTitle={currentQuestion.rightTitle}
          pairs={currentQuestion.pairs}
          onSolved={() => {
            handleScoreUpdate(score + 1);
            setGameOver(true);
          }}
          showNextButton={showNextForGame}
          onNext={nextQuestion}
          showFinishButton={showFinishForGame}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.FILLWORD && (
        <FillwordQuestion
          key={selectedQuestionIndex}
          question={currentQuestion.question}
          imgPath={currentQuestion.imgPath}
          correctWords={currentQuestion.correctWords}
          onWordFound={() => {
            handleScoreUpdate(score + 1);
          }}
          showNextButton={showNextForGame}
          onNext={nextQuestion}
          showFinishButton={showFinishForGame}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.PUZZLE && (
        <PuzzleQuestion
          key={selectedQuestionIndex}
          question={currentQuestion.question}
          imgPath={currentQuestion.imgPath}
          rows={currentQuestion.rows}
          columns={currentQuestion.columns}
          showPreview={currentQuestion.showPreview}
          onSolved={() => {
            handleScoreUpdate(score + 1);
            setGameOver(true);
          }}
          showNextButton={showNextForGame}
          onNext={nextQuestion}
          showFinishButton={showFinishForGame}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.JIGSAW && (
        <JigsawQuestion
          key={selectedQuestionIndex}
          question={currentQuestion.question}
          imgPath={currentQuestion.imgPath}
          rows={currentQuestion.rows}
          columns={currentQuestion.columns}
          showPreview={currentQuestion.showPreview}
          onSolved={() => {
            handleScoreUpdate(score + 1);
            setGameOver(true);
          }}
          showNextButton={showNextForGame}
          onNext={nextQuestion}
          showFinishButton={showFinishForGame}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.WORD_GRID && (
        <WordGridQuestion
          question={currentQuestion.question}
          instruction={currentQuestion.instruction}
          wordRows={currentQuestion.wordRows}
          revealedLetterColor={currentQuestion.revealedLetterColor}
          onSolved={() => {
            handleScoreUpdate(score + 1);
            setGameOver(true);
          }}
          showNextButton={showNextForGame}
          onNext={nextQuestion}
          showFinishButton={showFinishForGame}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.LOGICAL_SEQUENCE && (
        <LogicalSequenceQuestion
          question={currentQuestion.question}
          items={currentQuestion.items}
          questionKey={currentQuestion.id || selectedQuestionIndex}
          onOrderChange={selectOption}
          showCheckButton={state.showCheckButton}
          canCheck={Array.isArray(state.selectedOption) && state.selectedOption.length > 0}
          onCheck={() => checkAnswer("logicalSequence")}
          feedbackVisible={state.feedbackVisible}
          feedback={state.feedback}
          onCloseFeedback={closeFeedback}
          showNextButton={showNextForLogicalSequence}
          onNext={nextQuestion}
          showFinishButton={showFinishForLogicalSequence}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.PDF && (
        <PdfQuestion
          question={currentQuestion.question}
          pdfPath={currentQuestion.pdfPath}
          pdfFileName={currentQuestion.pdfFileName}
          showNextButton={showNextForPdf}
          onNext={nextQuestion}
          showFinishButton={showFinishForPdf}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.DND && (
        <GameQuestion
          questionId={currentQuestion.id || selectedQuestionIndex}
          score={score}
          question={currentQuestion.question}
          imgPath={currentQuestion.imgPath}
          videoPath={currentQuestion.videoPath}
          initialWords={currentQuestion.initialWords}
          leftBucketTitle={currentQuestion.leftBucketTitle}
          centerBucketTitle={currentQuestion.centerBucketTitle}
          rightBucketTitle={currentQuestion.rightBucketTitle}
          bucketsAmount={currentQuestion.bucketsAmount}
          onScoreUpdate={handleScoreUpdate}
          onGameOver={setGameOver}
          isGameOver={state.isGameOver}
          showNextButton={showNextForGame}
          onNext={nextQuestion}
          showFinishButton={showFinishForGame}
          onFinish={finishQuiz}
        />
      )}

      {kind === QUESTION_KIND.DND2 && (
        <GameQuestionDnd2
          questionId={currentQuestion.id || selectedQuestionIndex}
          score={score}
          question={currentQuestion.question}
          imgPath={currentQuestion.imgPath}
          videoPath={currentQuestion.videoPath}
          initialWords={currentQuestion.initialWords}
          buckets={currentQuestion.buckets}
          bucketCount={currentQuestion.bucketCount}
          bucketSize={currentQuestion.bucketSize}
          onScoreUpdate={handleScoreUpdate}
          onGameOver={setGameOver}
          isGameOver={state.isGameOver}
          showNextButton={showNextForGame}
          onNext={nextQuestion}
          showFinishButton={showFinishForGame}
          onFinish={finishQuiz}
        />
      )}
    </div>
  );
}

export default Module;
